import { THREE } from "./dependencies";
import { PARALLAX, introConfig, morphConfig } from "./config/motion";
import { sceneConfig } from "./config/scene";
import {
	accessibility,
	labels,
	messages,
	interactionConfig,
} from "./config/ui";
import { stage, fallback } from "./contact";
import { requireElement } from "./dom";
import { destinations } from "./scene/sculpture";
import { createScene } from "./scene/setup";
import { createMeshes } from "./scene/meshes";

try {
	const {
		renderer,
		scene,
		camera,
		linkHitboxes,
		raycaster,
		pointer,
		world,
		slab,
		flatSlabMaterial,
		litSlabMaterial,
		updateCardFrame,
	} = createScene(stage);
	const {
		starts,
		cell,
		floor,
		count,
		pixels,
		dummy,
		color,
		ink,
		destinationColors,
		white,
		stone,
		groundInk,
	} = createMeshes(world);
	let expanded = false,
		progress = 0,
		turn = 0,
		pitch = 0,
		last = performance.now();
	let targetTurn = 0,
		targetPitch = 0;
	function resetParallax() {
		targetTurn = 0;
		targetPitch = 0;
	}
	const reduced = matchMedia(interactionConfig.reducedMotionQuery);
	let autoExpandTimer: number | undefined;
	let manuallyInteracted = false;
	function setExpanded(value: boolean) {
		expanded = value;
		stage.setAttribute("aria-pressed", String(value));
		stage.setAttribute(
			"aria-label",
			value ? accessibility.collapse : accessibility.expand,
		);
		resetParallax();
	}
	function toggleManually() {
		manuallyInteracted = true;
		window.clearTimeout(autoExpandTimer);
		window.pixelIntro?.finish();
		setExpanded(!expanded);
	}
	function scheduleAutoExpand() {
		if (manuallyInteracted || !introConfig.autoExpand) return;
		autoExpandTimer = window.setTimeout(() => {
			if (!manuallyInteracted) setExpanded(true);
		}, introConfig.autoExpandDelayMs);
	}
	stage.addEventListener("keydown", (e) => {
		if (interactionConfig.toggleKeys.includes(e.key)) {
			e.preventDefault();
			toggleManually();
		}
	});
	function hitLink(event?: MouseEvent | PointerEvent) {
		if (
			!event ||
			!expanded ||
			progress < morphConfig.linkThreshold ||
			window.pixelIntro?.active ||
			!Number.isFinite(event.clientX) ||
			!Number.isFinite(event.clientY)
		)
			return undefined;
		const rect = stage.getBoundingClientRect();
		pointer.set(
			((event.clientX - rect.left) / rect.width) * 2 - 1,
			(-(event.clientY - rect.top) / rect.height) * 2 + 1,
		);
		camera.updateMatrixWorld(true);
		raycaster.setFromCamera(pointer, camera);
		return linkHitboxes.find(
			(link) => raycaster.intersectObject(link.mesh, false).length > 0,
		);
	}
	stage.addEventListener("click", (event) => {
		const link = hitLink(event);
		if (link) {
			window.location.href = link.href;
			return;
		}
		toggleManually();
	});
	stage.addEventListener("pointermove", (event) => {
		stage.title = hitLink(event)?.label ?? labels.toggleTooltip;
	});
	stage.addEventListener("pointerleave", () => {
		stage.title = "";
	});
	window.addEventListener("pointermove", (e) => {
		if (
			!PARALLAX.enabled ||
			!expanded ||
			reduced.matches ||
			window.pixelIntro?.active ||
			e.pointerType === "touch"
		)
			return;
		const x = THREE.MathUtils.clamp(
			(e.clientX / Math.max(window.innerWidth, 1)) * 2 - 1,
			-1,
			1,
		);
		const y = THREE.MathUtils.clamp(
			(e.clientY / Math.max(window.innerHeight, 1)) * 2 - 1,
			-1,
			1,
		);
		const yawLimit = x < 0 ? PARALLAX.maxLeftDeg : PARALLAX.maxRightDeg;
		targetTurn = -x * THREE.MathUtils.degToRad(Math.abs(yawLimit));
		targetPitch =
			y * THREE.MathUtils.degToRad(Math.abs(PARALLAX.maxPitchDeg));
	});
	window.addEventListener("pointerout", (e) => {
		if (!e.relatedTarget) resetParallax();
	});
	window.addEventListener("blur", resetParallax);
	window.addEventListener("pointercancel", resetParallax);
	let viewAspect = 1;
	let needsRender = true;
	let lastTurn = Number.NaN,
		lastPitch = Number.NaN;
	renderer.domElement.addEventListener("webglcontextrestored", () => {
		needsRender = true;
	});
	function resize() {
		const w = stage.clientWidth,
			h = stage.clientHeight;
		renderer.setSize(w, h);
		viewAspect = w / Math.max(h, 1);
		needsRender = true;
	}
	new ResizeObserver(resize).observe(stage);
	resize();
	let lastGeometryProgress = -1;
	function frame(now: number) {
		const dt = Math.min(
			(now - last) / 1000,
			morphConfig.maxFrameDeltaSeconds,
		);
		last = now;
		const target = expanded ? 1 : 0;
		const parallaxEnabled =
			PARALLAX.enabled &&
			expanded &&
			!reduced.matches &&
			!window.pixelIntro?.active;
		const yawTarget = parallaxEnabled ? targetTurn : 0;
		const pitchTarget = parallaxEnabled ? targetPitch : 0;
		turn = THREE.MathUtils.damp(turn, yawTarget, PARALLAX.response, dt);
		pitch = THREE.MathUtils.damp(pitch, pitchTarget, PARALLAX.response, dt);
		if (Math.abs(turn - yawTarget) < PARALLAX.snapTolerance)
			turn = yawTarget;
		if (Math.abs(pitch - pitchTarget) < PARALLAX.snapTolerance)
			pitch = pitchTarget;
		progress = reduced.matches
			? target
			: THREE.MathUtils.damp(progress, target, morphConfig.response, dt);
		if (Math.abs(progress - target) < morphConfig.snapTolerance)
			progress = target;
		if (
			expanded &&
			window.pixelIntro?.centered &&
			progress >= morphConfig.dockThreshold
		)
			progress = 1;
		if (expanded && progress >= morphConfig.dockThreshold)
			window.pixelIntro?.dock();
		// The CSS intro only moves the existing canvas. Leave the GPU idle while
		// it docks and the content fades in, and whenever the view is settled.
		if (
			!needsRender &&
			progress === lastGeometryProgress &&
			turn === lastTurn &&
			pitch === lastPitch
		) {
			requestAnimationFrame(frame);
			return;
		}
		const t = progress * progress * (3 - 2 * progress);
		// Keep the original display size: fewer modules now make each square larger.
		const framing = THREE.MathUtils.lerp(
			sceneConfig.camera.flatFraming,
			sceneConfig.camera.expandedFraming,
			t,
		);
		const extent = viewAspect < 1 ? framing / viewAspect : framing;
		camera.left = -extent * viewAspect;
		camera.right = extent * viewAspect;
		camera.top = extent;
		camera.bottom = -extent;
		camera.updateProjectionMatrix();
		const elevation = THREE.MathUtils.lerp(
			THREE.MathUtils.degToRad(sceneConfig.camera.flatElevationDeg),
			THREE.MathUtils.degToRad(sceneConfig.camera.expandedElevationDeg) +
				pitch,
			t,
		);
		const azimuth =
			(THREE.MathUtils.degToRad(sceneConfig.camera.expandedAzimuthDeg) +
				turn) *
			t;
		const focusY = sceneConfig.camera.focusY * t;
		camera.position.set(
			Math.sin(azimuth) *
				sceneConfig.camera.distance *
				Math.cos(elevation),
			focusY + sceneConfig.camera.distance * Math.sin(elevation),
			Math.cos(azimuth) *
				sceneConfig.camera.distance *
				Math.cos(elevation),
		);
		camera.lookAt(0, focusY, 0);
		// Static voxels and shadow maps need no buffer uploads while docking or hovering.
		if (progress !== lastGeometryProgress) {
			lastGeometryProgress = progress;
			// Match the CSS surface exactly when the QR is flat; light the open card.
			slab.material = t === 0 ? flatSlabMaterial : litSlabMaterial;
			slab.material.color.copy(white).lerp(stone, t);
			slab.scale.y = THREE.MathUtils.lerp(
				sceneConfig.base.flatScaleY,
				1,
				t,
			);
			slab.position.y =
				-sceneConfig.base.topOffset -
				(sceneConfig.base.height / 2) * slab.scale.y;
			floor.material.color.copy(ink).lerp(groundInk, t);
			for (let i = 0; i < count; i++) {
				const s = starts[i % starts.length],
					d = destinations[i % destinations.length];
				const exists = i < destinations.length;
				// Lower voxels rise first, followed by the higher lettering. No airborne burst.
				const delay =
					Math.min(d.y / morphConfig.heightReference, 1) *
					morphConfig.maxRiseDelay;
				const growth = THREE.MathUtils.smoothstep(
					progress,
					delay,
					delay + morphConfig.growthDuration,
				);
				const endScale = exists ? d.w : morphConfig.hiddenScale;
				dummy.position.set(
					THREE.MathUtils.lerp(s.x, d.x, growth),
					THREE.MathUtils.lerp(
						morphConfig.flatVoxelY,
						exists ? d.y : morphConfig.hiddenY,
						growth,
					),
					THREE.MathUtils.lerp(s.z, d.z, growth),
				);
				dummy.scale.set(
					THREE.MathUtils.lerp(cell, endScale, growth),
					THREE.MathUtils.lerp(
						morphConfig.flatVoxelHeight,
						exists ? d.h : morphConfig.hiddenScale,
						growth,
					),
					THREE.MathUtils.lerp(cell, endScale, growth),
				);
				dummy.updateMatrix();
				pixels.setMatrixAt(i, dummy.matrix);
				color
					.copy(ink)
					.lerp(destinationColors[i % destinations.length], growth);
				pixels.setColorAt(i, color);
			}
			pixels.instanceMatrix.needsUpdate = true;
			pixels.instanceColor!.needsUpdate = true;
			renderer.shadowMap.needsUpdate = true;
		}
		updateCardFrame((2 * extent) / Math.max(stage.clientHeight, 1));
		renderer.render(scene, camera);
		needsRender = false;
		lastTurn = turn;
		lastPitch = pitch;
		requestAnimationFrame(frame);
	}
	// Catch first-frame errors before removing the working QR.
	frame(performance.now());
	fallback.remove();
	document.querySelector("#loading")?.remove();
	if (document.readyState === "complete") scheduleAutoExpand();
	else window.addEventListener("load", scheduleAutoExpand, { once: true });
} catch (error) {
	window.pixelIntro?.finish();
	stage
		.querySelectorAll("canvas:not(.qr-fallback)")
		.forEach((canvas) => canvas.remove());
	document.querySelector("#loading")?.remove();
	const el = requireElement("#error");
	el.hidden = false;
	el.textContent = messages.noWebGL;
	console.error(error);
}
